import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight,
  ArrowUp, 
  MapPin,
  Calendar,
  Settings,
  Code,
  Layers,
  BarChart2,
  Cpu,
  Zap,
  Shield,
  Wrench,
  Users,
  Globe,
  Factory,
  Eye,
  Cog,
  ExternalLink
} from 'lucide-react';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Common components
import Container from './components/common/Container';
import SectionHeading from './components/common/SectionHeading';

// Sections
import CTABanner from './components/sections/CTABanner';

const GaloExpertisePage = () => {
  // State management
  const [scrolled, setScrolled] = useState(false);
  
  // Refs for scroll animations
  const expertiseRef = useRef(null);
  
  // Company links data
  const companyLinks = {
    'Ford Motor Company': 'https://www.ford.com',
    'Jaguar Land Rover': 'https://www.jaguarlandrover.com',
    'Amazon': 'https://www.amazon.com',
    'Volkswagen': 'https://www.volkswagen.com',
    'Porsche': 'https://www.porsche.com',
    'Valmet': 'https://www.valmet.com',
    'BMW': 'https://www.bmwgroup.com',
    'Bosch': 'https://www.bosch.com',
    'Magna': 'https://www.magna.com',
    'Gestamp': 'https://www.gestamp.com',
    'Nissan': 'https://www.nissan-global.com',
    'Audi': 'https://www.audi.com',
    'Varta': 'https://www.varta-ag.com',
    'Magnetto': 'https://www.magnetto.eu',
    'Scania': 'https://www.scania.com',
    'Volvo': 'https://www.volvogroup.com',
    'DAF': 'https://www.daf.com'
  };

  // Helper function to create company links
  const createCompanyLink = (companyName) => {
    const link = companyLinks[companyName];
    if (link) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-cyan-600 transition-colors duration-300 hover:underline inline-flex items-center gap-1"
        >
          {companyName}
          <ExternalLink className="w-3 h-3 opacity-60" />
        </a>
      );
    }
    return companyName;
  };
  


  // Our expertise areas
  const expertiseAreas = [
    {
      id: 1,
      title: "PLC Programming & Control Systems",
      category: "Software",
      description: "Advanced PLC programming for Allen-Bradley, Siemens, and other industrial controllers. Real-time control systems for complex manufacturing processes.",
      technologies: ["Allen-Bradley", "Siemens S7", "Safety PLCs", "HMI Development"],
      industries: ["Automotive", "Manufacturing", "Process Control"],
      icon: <Cpu className="w-6 h-6" />,
      image: "/img/code.webp",
      color: "from-blue-500 to-cyan-600"
    },
    {
      id: 2,
      title: "Robotic Cell Design & Programming",
      category: "Robotics",
      description: "Complete robotic cell design, programming, and optimization. Specializing in ABB, KUKA, and collaborative robotics for industrial applications.",
      technologies: ["ABB IRC5", "KUKA KRC", "Collaborative Robots", "Path Optimization"],
      industries: ["Automotive", "Assembly", "Welding"],
      icon: <Cog className="w-6 h-6" />,
      image: "/img/robot3.jpeg",
      color: "from-cyan-500 to-blue-600"
    },
    {
      id: 3,
      title: "Quality Control & Vision Systems",
      category: "Quality",
      description: "AI-powered quality control systems with advanced vision technology. Real-time inspection and defect detection for manufacturing processes.",
      technologies: ["Machine Vision", "AI Analytics", "Statistical Process Control", "Traceability"],
      industries: ["Automotive", "Electronics", "Precision Manufacturing"],
      icon: <Eye className="w-6 h-6" />,
      image: "/img/code.webp",
      color: "from-green-500 to-blue-600"
    },
    {
      id: 4,
      title: "Production Line Integration",
      category: "Integration",
      description: "End-to-end production line integration including material handling, conveyor systems, and process optimization for maximum efficiency.",
      technologies: ["SCADA Systems", "MES Integration", "IoT Connectivity", "Data Analytics"],
      industries: ["Automotive", "Logistics", "Manufacturing"],
      icon: <Factory className="w-6 h-6" />,
      image: "/img/robot6.jpeg",
      color: "from-purple-500 to-blue-600"
    },
    {
      id: 5,
      title: "Safety System Implementation",
      category: "Integration",
      description: "Comprehensive safety system design and implementation including safety PLCs, light curtains, and emergency stop systems.",
      technologies: ["Safety PLCs", "Risk Assessment", "Safety Relays", "Functional Safety"],
      industries: ["All Industrial Sectors"],
      icon: <Shield className="w-6 h-6" />,
      image: "/img/robot2.jpeg",
      color: "from-red-500 to-orange-600"
    },
    {
      id: 6,
      title: "Process Optimization & Analytics",
      category: "Software",
      description: "Data-driven process optimization using advanced analytics, machine learning, and real-time monitoring systems.",
      technologies: ["Data Analytics", "Machine Learning", "Real-time Monitoring", "KPI Dashboards"],
      industries: ["Manufacturing", "Process Industries", "Automotive"],
      icon: <BarChart2 className="w-6 h-6" />,
      image: "/img/code.webp",
      color: "from-indigo-500 to-purple-600"
    },
    {
      id: 7,
      title: "Welding System Automation",
      category: "Robotics",
      description: "Advanced welding automation systems including arc welding, spot welding, and laser welding with precise control and monitoring.",
      technologies: ["Robotic Welding", "Laser Systems", "Welding Monitoring", "Seam Tracking"],
      industries: ["Automotive", "Heavy Industry", "Structural"],
      icon: <Zap className="w-6 h-6" />,
      image: "/img/robot4.jpeg",
      color: "from-yellow-500 to-red-600"
    },
    {
      id: 8,
      title: "Equipment Design & Commissioning",
      category: "Integration",
      description: "Custom equipment design, manufacturing, and on-site commissioning for specialized industrial applications.",
      technologies: ["CAD Design", "Custom Machinery", "Installation", "Testing & Validation"],
      industries: ["Automotive", "Aerospace", "Special Purpose"],
      icon: <Wrench className="w-6 h-6" />,
      image: "/img/robot5.jpeg",
      color: "from-gray-600 to-blue-600"
    }
  ];

  // Industry sectors we serve
  const industrySectors = [
    {
      name: "Automotive",
      description: "Complete automation solutions for vehicle manufacturing",
      companies: ["Ford Motor Company", "Jaguar Land Rover", "Volkswagen", "BMW", "Audi", "Porsche", "Nissan"],
      projects: "25+ Projects",
      icon: <Settings className="w-8 h-8" />,
      color: "from-blue-500 to-cyan-600"
    },
    {
      name: "Manufacturing",
      description: "Industrial automation across diverse manufacturing sectors",
      companies: ["Bosch", "Magna", "Gestamp", "Valmet", "Varta"],
      projects: "15+ Projects", 
      icon: <Factory className="w-8 h-8" />,
      color: "from-green-500 to-blue-600"
    },
    {
      name: "Logistics & E-commerce",
      description: "Automated systems for warehousing and distribution",
      companies: ["Amazon", "Magnetto"],
      projects: "8+ Projects",
      icon: <Globe className="w-8 h-8" />,
      color: "from-orange-500 to-red-600"
    },
    {
      name: "Transportation",
      description: "Automation solutions for commercial vehicle production",
      companies: ["Scania", "Volvo", "DAF"],
      projects: "12+ Projects",
      icon: <Users className="w-8 h-8" />,
      color: "from-purple-500 to-indigo-600"
    }
  ];


  
  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Animation on scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );
    
    if (expertiseRef.current) {
      observer.observe(expertiseRef.current);
    }
    
    return () => {
      if (expertiseRef.current) {
        observer.unobserve(expertiseRef.current);
      }
    };
  }, []);

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* SEO Metadata */}
      <Helmet>
        <title>What We Do | Galo Industries - Industrial Automation Expertise</title>
        <meta
          name="description"
          content="Discover Galo Industries' comprehensive automation expertise including PLC programming, robotics, quality control, and system integration for leading manufacturers."
        />
      </Helmet>

      {/* Header */}
      <Header current="expertise" />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.15)_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-300/10 to-blue-300/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-500/10 to-indigo-300/5 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        
        <Container className="relative z-10">
          {/* Breadcrumb */}
          <div className="mb-8">
            <div className="flex items-center text-blue-100 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">What We Do</span>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              What <span className="relative">
                <span className="text-cyan-200">We Do</span>
                <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-cyan-400/80 to-blue-300/60 rounded-full"></span>
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 mb-12 leading-relaxed">
              Comprehensive industrial automation solutions delivered by experts who understand real manufacturing challenges.
            </p>
          </div>
        </Container>
      </section>
      
      {/* Expertise Areas Section */}
      <section className="py-20 bg-white" ref={expertiseRef}>
        <Container>
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="Our Expertise"
              title={<>Our Core <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Capabilities</span></>}
              description={`Discover ${expertiseAreas.length} specialized automation capabilities`}
              centered
            />
            
            {/* Expertise Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mt-12 expertise-grid">
              {expertiseAreas.map(area => (
                <div 
                  key={area.id} 
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full expertise-card"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={area.image} 
                      alt={area.title} 
                      className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${area.color} opacity-80`}></div>
                    
                    <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
                      <div>
                        <div className={`inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mb-4`}>
                          {area.icon}
                        </div>
                        <span className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                          {area.category}
                        </span>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">
                          {area.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <p className="text-slate-600 mb-4 flex-grow leading-relaxed">
                      {area.description}
                    </p>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-slate-900 mb-2">Technologies:</h4>
                      <div className="flex flex-wrap gap-2">
                        {area.technologies.map((tech, index) => (
                          <span key={index} className="bg-slate-100 px-3 py-1 rounded-full text-slate-600 text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-2">Industries:</h4>
                      <div className="flex flex-wrap gap-2">
                        {area.industries.map((industry, index) => (
                          <span key={index} className="bg-cyan-50 text-cyan-700 px-3 py-1 rounded-full text-xs font-medium">
                            {industry}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
      
      {/* Industry Sectors */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <Container>
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="Industry Focus"
              title={<>Sectors We <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Serve</span></>}
              description="Deep expertise across key industrial sectors"
              centered
            />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
              {industrySectors.map((sector, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${sector.color} text-white rounded-xl mb-4 shadow-lg`}>
                    {sector.icon}
                  </div>
                  
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{sector.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{sector.description}</p>
                  
                  <div className="mb-4">
                    <span className="inline-block bg-cyan-50 text-cyan-700 px-3 py-1 rounded-full text-xs font-medium">
                      {sector.projects}
                    </span>
                  </div>
                  
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Key Partners:</h4>
                    <div className="text-sm text-slate-600 space-y-1">
                      {sector.companies.map((company, idx) => (
                        <div key={idx}>
                          {createCompanyLink(company)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
      
      {/* Partner Ecosystem */}
      <section className="py-20 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto text-center">
            <SectionHeading
              eyebrow="Partner Ecosystem"
              title={<>Trusted by <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Industry Leaders</span></>}
              description="Building the future of manufacturing together"
              centered
            />
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mt-12 items-center justify-items-center">
              {Object.keys(companyLinks).map((company, index) => (
                <div key={index} className="group">
                  <a
                    href={companyLinks[company]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 rounded-xl bg-slate-50 hover:bg-cyan-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 w-full"
                  >
                    <div className="text-slate-700 font-medium text-center text-sm group-hover:text-cyan-700 transition-colors">
                      {company}
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
      
      {/* CTA Banner */}
      <CTABanner
        title="Ready to transform your operations?"
        body="Let's discuss how our automation expertise can enhance your manufacturing processes."
        primary={{ label: 'Contact Us', href: '/contact' }}
        secondary={{ label: 'View Case Studies', href: '/case-studies' }}
      />
      
      {/* Footer */}
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
      
      <style jsx>{`
        /* Animation for slow pulse */
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.3; }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
        
        /* Hide scrollbar */
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Scroll animation classes */
        .expertise-grid {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        
        .is-visible .expertise-grid {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Card animations */
        .expertise-card {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease-out, transform 0.5s ease-out;
        }
        
        .is-visible .expertise-card {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Staggered delays */
        .is-visible .expertise-card:nth-child(2n+1) { transition-delay: 0.1s; }
        .is-visible .expertise-card:nth-child(2n+2) { transition-delay: 0.2s; }
      `}</style>
    </div>
  );
};

export default GaloExpertisePage;